import { metaForPath } from "../../lib/docs/source-index";

// The auto-generated "Source & API" card that sits above the hand-written
// module spec in the docs pane. Everything here is regex-extracted from
// source at build/dev time — see src/lib/docs/source-index.ts. If it looks
// out of date, rebuild. If it looks wrong (a rename slipped past the
// regex), fix the extractor in source-index.ts, not the spec doc.

interface Props {
  pathname: string;
}

export default function SourceCard({ pathname }: Props) {
  const meta = metaForPath(pathname);
  if (!meta) return null;

  const hasApi = meta.lensApiCalls.length > 0;
  const hasStack = meta.stackApiCalls.length > 0;
  const hasStore = meta.storeReads.length > 0;
  const hasClients = meta.apiClientImports.length > 0;

  return (
    <section className="mb-6 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-4 text-xs space-y-3">
      <header className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
          Source &amp; API
        </span>
        <span
          className="text-[10px] text-slate-400 dark:text-slate-500"
          title="Auto-extracted from source at build time. If it looks wrong, fix source-index.ts."
        >
          auto
        </span>
      </header>

      <Row label="Route">
        <code className="font-mono text-[11px] text-slate-700 dark:text-slate-200">
          {meta.route}
        </code>
      </Row>

      <Row label="File">
        <a
          href={meta.githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline break-all"
        >
          {meta.file}
        </a>
      </Row>

      {hasStore && (
        <Row label="Store reads">
          <ChipList items={meta.storeReads} />
        </Row>
      )}

      {hasClients && (
        <Row label="Domain clients">
          <ChipList items={meta.apiClientImports} />
        </Row>
      )}

      {hasApi && (
        <Row label="Lens API calls">
          <ChipList items={meta.lensApiCalls} mono />
        </Row>
      )}

      {hasStack && (
        <Row label="Stack API calls">
          <ChipList items={meta.stackApiCalls.map((m) => `stackApi.${m}()`)} mono />
        </Row>
      )}

      {!hasStore && !hasClients && !hasApi && !hasStack && (
        <p className="text-slate-500 dark:text-slate-400">
          No store or API touchpoints extracted. If the screen makes calls, the extractor
          may need widening in <code className="font-mono text-[10px]">source-index.ts</code>.
        </p>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 sm:w-24 sm:flex-none">
        {label}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function ChipList({ items, mono = false }: { items: string[]; mono?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span
          key={item}
          className={`inline-block rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-700 dark:text-slate-200 ${
            mono ? "font-mono" : ""
          }`}
        >
          {item}
        </span>
      ))}
    </div>
  );
}
